import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Card,
    CardContent,
    IconButton,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getAllUsers } from '../../api/admin_api';
import { getAccountsByUserId } from '../../api/account_api';
import { addStrategy } from '../../api/strategyApi';
import CreateStrategyWizard from './CreateStrategyWizard';

const AssignStrategyContent = ({ template, onBack }) => {
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedAccount, setSelectedAccount] = useState('');
    const [users, setUsers] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showCustomizeWizard, setShowCustomizeWizard] = useState(false);
    const [showAllParameters, setShowAllParameters] = useState(false);

    // Fetch users only on component mount (template is passed as prop)
    useEffect(() => {
        fetchUsers();
    }, []);

    // Fetch accounts when user is selected
    useEffect(() => {
        if (selectedUser) {
            // selectedUser is now email, need to find userId
            const user = users.find(u => u.email === selectedUser);
            if (user && user.id) {
                fetchAccountsForUser(user.id);
            }
        } else {
            setAccounts([]);
            setSelectedAccount('');
        }
    }, [selectedUser, users]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await getAllUsers();

            // Handle different response formats
            const usersData = Array.isArray(response) ? response : (response?.data || []);
            setUsers(usersData);

            console.log('Loaded users from API:', usersData);
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Failed to load users');
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAccountsForUser = async (userId) => {
        try {
            setAccountsLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await getAccountsByUserId(token, userId);
            if (response && response.success && response.data) {
                setAccounts(response.data);
            } else {
                setAccounts([]);
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
            toast.error('Failed to load accounts');
            setAccounts([]);
        } finally {
            setAccountsLoading(false);
        }
    };

    // Template is passed as prop - no need to fetch strategies

    // Helper function to build parameters from template defaults
    const buildParametersFromTemplate = (template) => {
        const params = {
            version: template.version || 'v1',
            Enabled: true,
            Mode: 1
        };

        // Extract default symbols from template parameters
        let futuresSymbol = 'XAUUSD';
        let spotSymbol = 'XAGUSD';

        template.parameters.forEach(param => {
            if (param.name === 'FuturesSymbol') {
                futuresSymbol = param.defaultValue || futuresSymbol;
            } else if (param.name === 'SpotSymbol') {
                spotSymbol = param.defaultValue || spotSymbol;
            }
        });

        params.FuturesSymbol = futuresSymbol;
        params.SpotSymbol = spotSymbol;

        // Map all template parameters to backend format
        template.parameters.forEach(param => {
            if (param.name === 'FuturesSymbol' || param.name === 'SpotSymbol') {
                // Already handled above
                return;
            }

            const defaultValue = param.defaultValue;

            // Handle parameter mapping based on name structure
            if (param.name.includes('.')) {
                // Already namespaced (e.g., "GridManagement.BaseLotSize")
                params[param.name] = defaultValue;
            } else if (param.group === 'Grid Management') {
                // Add GridManagement namespace
                params[`GridManagement.${param.name}`] = defaultValue;
            } else if (param.group === 'Risk Management') {
                params[`RiskManagement.${param.name}`] = defaultValue;
            } else if (param.group === 'Spread Validation') {
                params[`SpreadValidation.${param.name}`] = defaultValue;
            } else if (param.group === 'Core Settings') {
                // Core settings go directly
                params[param.name] = defaultValue;
            } else {
                // Default behavior
                params[param.name] = defaultValue;
            }
        });

        // Add default risk management if not present
        if (!params['RiskManagement.Enabled']) {
            params['RiskManagement.Enabled'] = false;
            params['RiskManagement.OrderCooldownMs'] = 220;
            params['RiskManagement.OrderCooldownSeconds'] = 0;
            params['RiskManagement.RequireMinimumBars'] = false;
            params['RiskManagement.WarmupMinutes'] = 0;
        }

        // Add default spread validation if not present
        if (!params['SpreadValidation.Enabled']) {
            params['SpreadValidation.Enabled'] = true;
            params['SpreadValidation.MinAcceptableSpread'] = -1000;
            params['SpreadValidation.MaxAcceptableSpread'] = 1000;
            params['SpreadValidation.ExpectedAverageSpread'] = 0;
        }

        return params;
    };

    // Handler for "Use Defaults" button
    const handleUseDefaults = () => {
        // Validate user and account are selected
        if (!selectedUser || !selectedAccount) {
            toast.error('Please select user and account first');
            return;
        }
        if (!template) {
            toast.error('No template selected');
            return;
        }
        // Show confirmation dialog
        setShowConfirmDialog(true);
    };

    // Handler for "Customize" button
    const handleCustomize = () => {
        // Validate user and account are selected
        if (!selectedUser || !selectedAccount) {
            toast.error('Please select user and account first');
            return;
        }
        if (!template) {
            toast.error('No template selected');
            return;
        }
        // Open customize wizard
        setShowCustomizeWizard(true);
    };

    const handleAssign = async () => {
        // Validation
        if (!selectedUser || !selectedAccount) {
            toast.error('Please select user and account');
            return;
        }

        if (!template) {
            toast.error('No template selected');
            return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
            toast.error('Authentication token not found');
            return;
        }

        try {
            setLoading(true);

            // Build parameters from template defaults
            const parameters = buildParametersFromTemplate(template);

            // Get symbols from parameters
            const futuresSymbol = parameters.FuturesSymbol || 'XAUUSD';
            const spotSymbol = parameters.SpotSymbol || 'XAGUSD';

            // Prepare strategy payload matching backend API format
            const strategyPayload = {
                name: `${template.name} - ${getSelectedUserName()}`,
                description: template.description || 'Strategy assigned from template',
                accountNumber: parseInt(selectedAccount),
                strategyType: template.strategyType || 'goldarb',
                isEnabled: true,
                isMultiSymbol: template.isMultiSymbol !== undefined ? template.isMultiSymbol : true,
                symbols: [futuresSymbol, spotSymbol],
                parameters: parameters,
                createdBy: 'admin'
            };

            console.log('Assigning strategy with payload:', strategyPayload);

            // Call backend API to create strategy
            const result = await addStrategy(token, strategyPayload);

            if (result.success || result.data) {
                toast.success(`Strategy "${template.name}" assigned successfully to ${getSelectedUserName()}!`);

                // Close confirmation dialog
                setShowConfirmDialog(false);

                // Reset form
                setSelectedUser('');
                setSelectedAccount('');

                // Navigate back after short delay
                setTimeout(() => {
                    if (onBack) onBack();
                }, 1500);
            } else {
                toast.error(result.error || 'Failed to assign strategy');
            }
        } catch (error) {
            console.error('Error assigning strategy:', error);
            toast.error(error.message || 'Failed to assign strategy. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getSelectedUserName = () => {
        const user = users.find(u => u.email === selectedUser);
        if (!user) return '';
        return `${user.firstName || user.name || ''} ${user.lastName || ''}`.trim();
    };

    const getFilteredAccounts = () => {
        if (!selectedUser) return [];
        return accounts;
    };

    return (
        <Box sx={{ background: 'rgba(51, 53, 57, 1)', minHeight: '100vh', padding: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 3 }}>
                <IconButton
                    onClick={onBack}
                    sx={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#ffffff',
                        '&:hover': { background: 'rgba(255, 255, 255, 0.2)' }
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                        Assign Strategy
                    </Typography>
                </Box>
            </Box>

            {/* User Selection */}
            <Box sx={{ marginBottom: 3 }}>
                <Typography variant="subtitle1" sx={{ marginBottom: 1.5, fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                    Select User
                </Typography>
                <FormControl fullWidth disabled={loading}>
                    <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {loading ? 'Loading users...' : 'Select User'}
                    </InputLabel>
                    <Select
                        value={selectedUser}
                        label={loading ? 'Loading users...' : 'Select User'}
                        onChange={(e) => {
                            setSelectedUser(e.target.value);
                            setSelectedAccount('');
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    background: 'rgba(41, 43, 47, 1)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    '& .MuiMenuItem-root': {
                                        color: '#ffffff',
                                        '&:hover': {
                                            background: 'rgba(102, 126, 234, 0.2)'
                                        },
                                        '&.Mui-selected': {
                                            background: 'rgba(102, 126, 234, 0.3)',
                                            '&:hover': {
                                                background: 'rgba(102, 126, 234, 0.4)'
                                            }
                                        }
                                    }
                                }
                            }
                        }}
                        sx={{
                            background: 'rgba(41, 44, 49, 1)',
                            color: '#ffffff',
                            '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
                            '.MuiSvgIcon-root': { color: '#ffffff' },
                            '&.Mui-disabled': {
                                background: 'rgba(41, 44, 49, 0.5)',
                                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.1)' }
                            }
                        }}
                    >
                        {loading ? (
                            <MenuItem disabled>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={20} sx={{ color: '#667eea' }} />
                                    <Typography>Loading users...</Typography>
                                </Box>
                            </MenuItem>
                        ) : users.length === 0 ? (
                            <MenuItem disabled>
                                <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>No users found</Typography>
                            </MenuItem>
                        ) : (
                            users.map((user) => (
                                <MenuItem key={user.id} value={user.email}>
                                    {user.firstName || user.name} {user.lastName || ''} ({user.email})
                                </MenuItem>
                            ))
                        )}
                    </Select>
                </FormControl>
            </Box>

            {/* Account Selection */}
            <Box sx={{ marginBottom: 3 }}>
                <Typography variant="subtitle1" sx={{ marginBottom: 1.5, fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                    Select Account
                </Typography>
                <FormControl fullWidth disabled={!selectedUser || accountsLoading}>
                    <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {!selectedUser ? 'Select a user first' : accountsLoading ? 'Loading accounts...' : 'Select Account'}
                    </InputLabel>
                    <Select
                        value={selectedAccount}
                        label={!selectedUser ? 'Select a user first' : accountsLoading ? 'Loading accounts...' : 'Select Account'}
                        onChange={(e) => setSelectedAccount(e.target.value)}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    background: 'rgba(41, 43, 47, 1)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    '& .MuiMenuItem-root': {
                                        color: '#ffffff',
                                        '&:hover': {
                                            background: 'rgba(102, 126, 234, 0.2)'
                                        },
                                        '&.Mui-selected': {
                                            background: 'rgba(102, 126, 234, 0.3)',
                                            '&:hover': {
                                                background: 'rgba(102, 126, 234, 0.4)'
                                            }
                                        }
                                    }
                                }
                            }
                        }}
                        sx={{
                            background: 'rgba(41, 44, 49, 1)',
                            color: '#ffffff',
                            '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
                            '.MuiSvgIcon-root': { color: '#ffffff' },
                            '&.Mui-disabled': {
                                background: 'rgba(41, 44, 49, 0.5)',
                                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.1)' }
                            }
                        }}
                    >
                        {accountsLoading ? (
                            <MenuItem disabled>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={20} sx={{ color: '#667eea' }} />
                                    <Typography>Loading accounts...</Typography>
                                </Box>
                            </MenuItem>
                        ) : !selectedUser ? (
                            <MenuItem disabled>
                                <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Please select a user first</Typography>
                            </MenuItem>
                        ) : getFilteredAccounts().length === 0 ? (
                            <MenuItem disabled>
                                <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>No accounts found for this user</Typography>
                            </MenuItem>
                        ) : (
                            getFilteredAccounts().map((account) => (
                                <MenuItem key={account.id} value={account.accountNumber}>
                                    {account.accountNumber} - {account.accountName} ({account.accountType})
                                </MenuItem>
                            ))
                        )}
                    </Select>
                </FormControl>
            </Box>

            {/* Template Settings Preview */}
            <Box sx={{ marginBottom: 3 }}>
                <Typography variant="subtitle1" sx={{ marginBottom: 2, fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                    Template Settings Preview
                </Typography>
                {template ? (
                    <Card sx={{
                        background: 'rgba(41, 43, 47, 1)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                        <CardContent>
                            {/* Template Header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                                <Box sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: '8px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '2rem'
                                }}>
                                    {template.icon || '🎯'}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" sx={{ color: '#667eea', marginBottom: 0.5, fontWeight: 700 }}>
                                        {template.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                        {template.description}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Template Parameters Preview */}
                            <Box sx={{
                                marginTop: 3,
                                paddingTop: 2,
                                borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                                <Typography variant="subtitle2" sx={{
                                    marginBottom: 2,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontWeight: 600
                                }}>
                                    Default Parameters:
                                </Typography>
                                <Grid container spacing={2}>
                                    {template.parameters && (showAllParameters ? template.parameters : template.parameters.slice(0, 12)).map((param, index) => (
                                        <Grid item xs={12} sm={6} md={4} key={index}>
                                            <Box sx={{
                                                padding: 1.5,
                                                background: 'rgba(31, 33, 37, 1)',
                                                borderRadius: '8px',
                                                border: '1px solid rgba(255, 255, 255, 0.05)'
                                            }}>
                                                <Typography variant="caption" sx={{
                                                    color: 'rgba(255, 255, 255, 0.5)',
                                                    display: 'block',
                                                    fontSize: '0.7rem',
                                                    marginBottom: 0.5
                                                }}>
                                                    {param.label || param.name}
                                                </Typography>
                                                <Typography variant="body2" sx={{
                                                    color: 'rgba(255, 255, 255, 0.9)',
                                                    fontWeight: 600
                                                }}>
                                                    {param.defaultValue !== undefined && param.defaultValue !== null
                                                        ? String(param.defaultValue)
                                                        : 'Not set'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                                {template.parameters && template.parameters.length > 12 && (
                                    <Box
                                        onClick={() => setShowAllParameters(!showAllParameters)}
                                        sx={{
                                            marginTop: 2,
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            padding: 1,
                                            borderRadius: '8px',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                background: 'rgba(102, 126, 234, 0.1)'
                                            }
                                        }}
                                    >
                                        <Typography variant="caption" sx={{
                                            color: '#667eea',
                                            fontWeight: 600
                                        }}>
                                            {showAllParameters
                                                ? '▲ Show Less'
                                                : `▼ Show ${template.parameters.length - 12} More Parameters`}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* Action Buttons */}
                            <Box sx={{
                                display: 'flex',
                                gap: 2,
                                marginTop: 3,
                                paddingTop: 2,
                                borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleUseDefaults}
                                    disabled={!selectedUser || !selectedAccount}
                                    sx={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        textTransform: 'none',
                                        padding: '10px 24px',
                                        fontWeight: 600,
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
                                        },
                                        '&:disabled': {
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            color: 'rgba(255, 255, 255, 0.3)'
                                        }
                                    }}
                                >
                                    Use Default Settings
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={handleCustomize}
                                    disabled={!selectedUser || !selectedAccount}
                                    sx={{
                                        borderColor: '#667eea',
                                        color: '#667eea',
                                        textTransform: 'none',
                                        padding: '10px 24px',
                                        fontWeight: 600,
                                        '&:hover': {
                                            borderColor: '#5568d3',
                                            background: 'rgba(102, 126, 234, 0.1)'
                                        },
                                        '&:disabled': {
                                            borderColor: 'rgba(255, 255, 255, 0.2)',
                                            color: 'rgba(255, 255, 255, 0.3)'
                                        }
                                    }}
                                >
                                    Customize Parameters
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                ) : (
                    <Box sx={{
                        padding: 4,
                        textAlign: 'center',
                        background: 'rgba(41, 43, 47, 1)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            No template selected
                        </Typography>
                    </Box>
                )}
            </Box>


            {/* Confirmation Dialog */}
            <Dialog
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        background: 'rgba(31, 33, 37, 1)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                    }
                }}
            >
                <DialogTitle sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '1.25rem'
                }}>
                    📋 Confirm Strategy Assignment
                </DialogTitle>
                <DialogContent sx={{ marginTop: 3 }}>
                    <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: 2 }}>
                        Please review the details before assigning:
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{
                            padding: 2,
                            background: 'rgba(41, 43, 47, 1)',
                            borderRadius: '8px',
                            border: '1px solid rgba(255, 255, 255, 0.05)'
                        }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>User</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#667eea' }}>
                                {getSelectedUserName()}
                            </Typography>
                        </Box>

                        <Box sx={{
                            padding: 2,
                            background: 'rgba(41, 43, 47, 1)',
                            borderRadius: '8px',
                            border: '1px solid rgba(255, 255, 255, 0.05)'
                        }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>Account</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                                {selectedAccount}
                            </Typography>
                        </Box>

                        <Box sx={{
                            padding: 2,
                            background: 'rgba(41, 43, 47, 1)',
                            borderRadius: '8px',
                            border: '1px solid rgba(255, 255, 255, 0.05)'
                        }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>Strategy</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                                {template?.icon} {template?.name}
                            </Typography>
                        </Box>

                        <Box sx={{
                            padding: 2,
                            background: 'rgba(41, 43, 47, 1)',
                            borderRadius: '8px',
                            border: '1px solid rgba(255, 255, 255, 0.05)'
                        }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>Symbols</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                                {template?.parameters?.find(p => p.name === 'FuturesSymbol')?.defaultValue || 'XAUUSD'} - {template?.parameters?.find(p => p.name === 'SpotSymbol')?.defaultValue || 'XAGUSD'}
                            </Typography>
                        </Box>

                        <Box sx={{
                            padding: 2,
                            background: 'rgba(41, 43, 47, 1)',
                            borderRadius: '8px',
                            border: '1px solid rgba(255, 255, 255, 0.05)'
                        }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>Parameters</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#22c55e' }}>
                                ✓ Default Settings ({template?.parameters?.length || 0} parameters)
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: 3, paddingTop: 2 }}>
                    <Button
                        onClick={() => setShowConfirmDialog(false)}
                        sx={{
                            textTransform: 'none',
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': { background: 'rgba(255, 255, 255, 0.1)' }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAssign}
                        disabled={loading}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
                            },
                            '&:disabled': {
                                background: 'rgba(255, 255, 255, 0.1)',
                                color: 'rgba(255, 255, 255, 0.3)'
                            }
                        }}
                    >
                        {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : '✓ Assign Strategy'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Customize Wizard */}
            <CreateStrategyWizard
                open={showCustomizeWizard}
                onClose={() => {
                    setShowCustomizeWizard(false);
                }}
                onSave={() => {
                    setShowCustomizeWizard(false);
                    // After successful customization, close the parent modal too
                    if (onBack) onBack();
                }}
                template={template}
                preSelectedUser={selectedUser}
                preSelectedAccount={selectedAccount}
            />
        </Box>
    );
};

export default AssignStrategyContent;
